import React, { useEffect, useState } from "react";
import { Drawer, Tabs, Avatar, Typography } from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  ExpandOutlined,
  EditOutlined,
  ShareAltOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  TagOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import CommentsSection from "./CommentSection";
import AttachmentsSection from "./AttachmentsSection";
import useTasks from "hooks/projects/useTasks";
import dayjs from "dayjs";
import { useAuth } from "contexts/AuthContext";
import { useTags } from "hooks";
import { SimpleTagManager } from "./SimpleTagManager";
import useFormattedDate from "hooks/useFormatDate";

const { Title, Text, Paragraph } = Typography;

const statusMap = {
  OPEN: { label: "Open", color: "#9ca3af" }, // gray
  IN_PROGRESS: { label: "In Progress", color: "#3b82f6" }, // blue
  COMPLETED: { label: "Completed", color: "#22c55e" }, // green
  BLOCKED: { label: "Blocked", color: "#ef4444" }, // red
};

const TaskDrawer = ({ visible, task, onClose, onEditTask, onTagsUpdate }) => {
  const {
    fetchTaskComments,
    addTaskComment,
    updateTaskComment,
    deleteTaskComment,
    fetchTaskLinks,
    addTaskLink,
    loading,
  } = useTasks();
  const { tags: allTags } = useTags();
  const { user } = useAuth();
  const { firstName, lastName } = user || {};
  const fullName = `${firstName} ${lastName}`;

  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllMeta, setShowAllMeta] = useState(false);
  const [comments, setComments] = useState([]);
  const [taskLinks, setTaskLinks] = useState([]);
  const [localTags, setLocalTags] = useState(task?.tags || []);

  const { formatDateTime } = useFormattedDate();

  useEffect(() => {
    setLocalTags(task?.tags || []);
  }, [task]);

  useEffect(() => {
    if (!visible) return;

    // Clear previous task data immediately
    setComments([]);
    setTaskLinks([]);

    if (!task?.id || !task?.projectId) return;

    const loadData = async () => {
      try {
        const [commentsData, linksData] = await Promise.all([
          fetchTaskComments(task.projectId, task.id),
          fetchTaskLinks(task.id),
        ]);
        setComments(commentsData || []);
        setTaskLinks(linksData || []);
      } catch (err) {
        console.error("Failed to load comments", err);
        setComments([]);
        setTaskLinks([]);
      }
    };

    loadData();
  }, [visible, task?.id, task?.projectId]);

  if (!task) return null;

  // all meta rows
  const metaItems = [
    {
      icon: <CheckCircleOutlined />,
      label: "Status",
      value: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: statusMap[task.status]?.color || "#9ca3af",
              display: "inline-block",
            }}
          />
          <Text style={{ fontWeight: 500 }}>{task.status}</Text>
        </div>
      ),
    },
    {
      icon: <CalendarOutlined />,
      label: "Due date",
      value: <Text>{task.expectedCompletionDate || "—"}</Text>,
    },
    {
      icon: <UserOutlined />,
      label: "Assignee",
      value: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar size={22}>
            {task.assigneeName
              ?.split(" ")
              .map((n) => n[0])
              .join("")}
          </Avatar>
          <Text>{task.assigneeName}</Text>
        </div>
      ),
    },
    {
      icon: <TagOutlined />,
      label: "Tags",
      value: (
        <SimpleTagManager
          entityType="task"
          entityId={task.id}
          projectId={task.projectId}
          currentTags={localTags}
          availableTags={allTags}
          onChange={(updatedTags) => {
            setLocalTags(updatedTags);
          }}
        />
      ),
    },
    {
      icon: <FileTextOutlined />,
      label: "Priority",
      value: <Text>{task.priority}</Text>,
    },
    {
      icon: <CheckSquareOutlined />,
      label: "Points",
      value: <Text>{task.points}</Text>,
    },
    {
      icon: <UserOutlined />,
      label: "Created by",
      value: <Text>{task.createdByName}</Text>,
    },
    {
      icon: <CalendarOutlined />,
      label: "Started at",
      value: <Text>{formatDateTime(task.startedAt) || "—"}</Text>,
    },
    {
      icon: <CalendarOutlined />,
      label: "Expected Completion",
      value: <Text>{formatDateTime(task.expectedCompletionDate) || "—"}</Text>,
    },
    {
      icon: <CalendarOutlined />,
      label: "Actual Completion",
      value: <Text>{formatDateTime(task.actualCompletionDate) || "—"}</Text>,
    },
    task.iteration
      ? {
          icon: <CalendarOutlined />,
          label: "Iteration",
          value: <Text>{task.iteration.name}</Text>,
        }
      : null,
    task.assignmentStatus
      ? {
          icon: <CheckCircleOutlined />,
          label: "Assignment status",
          value: <Text>{task.assignmentStatus}</Text>,
        }
      : null,
  ].filter(Boolean);

  // number of items to show initially
  const visibleCount = 6;

  const handleAddComment = async (content) => {
    const tempId = `temp-${Date.now()}`;

    const optimisticComment = {
      id: Date.now(),
      createdByName: fullName,
      avatar: "YO",
      content,
      createdAt: dayjs().toISOString(),
    };

    setComments((prev) => [optimisticComment, ...prev]);

    try {
      const saved = await addTaskComment(task.projectId, task.id, { content });
      setComments((prev) => prev.map((c) => (c.id === tempId ? saved : c)));
    } catch (err) {
      // rollback
      setComments((prev) => prev.filter((c) => c.id !== tempId));
    }
  };

  const handleEditComment = async (commentId, content) => {
    try {
      const updated = await updateTaskComment(
        task.projectId,
        task.id,
        commentId,
        { content },
      );

      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? updated : c)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteTaskComment(task.projectId, task.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddLink = async (values) => {
    if (!values.label || !values.url) return;

    const newLink = {
      label: values.label,
      url: values.url,
    };

    // optimistic update
    const tempId = `temp-${Date.now()}`;
    setTaskLinks((prev) => [...prev, { ...newLink, id: tempId }]);

    try {
      const saved = await addTaskLink(task.id, newLink);
      setTaskLinks((prev) => prev.map((l) => (l.id === tempId ? saved[0] : l)));
    } catch (err) {
      console.error("Failed to add link", err);
      // rollback
      setTaskLinks((prev) => prev.filter((l) => l.id !== tempId));
    }
  };

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? 520 : "100vw"}
      zIndex={99999}
      styles={{ padding: 24 }}
      title={null}
      closable={false}
      destroyOnHidden
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 700,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* Breadcrumb / page context */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Craftboard Project</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              On Progress
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <EditOutlined
              className="task-drawer-icon"
              onClick={() => onEditTask(task)}
            />
            {/* <ShareAltOutlined className="task-drawer-icon" />
            <MoreOutlined className="task-drawer-icon" /> */}
            <CloseCircleOutlined
              className="task-drawer-icon"
              onClick={onClose}
            />
          </div>
        </div>

        {/* Title */}
        <Title level={4} style={{ marginBottom: 16 }}>
          {task.title}
        </Title>

        {/* Status / Meta section */}
        <div style={{ marginBottom: 20 }}>
          {(showAllMeta ? metaItems : metaItems.slice(0, visibleCount)).map(
            (item, index) => (
              <MetaRow
                key={index}
                icon={item.icon}
                label={item.label}
                value={item.value}
              />
            ),
          )}

          {metaItems.length > visibleCount && (
            <Text
              onClick={() => setShowAllMeta((prev) => !prev)}
              style={{
                cursor: "pointer",
                color: "#3b82f6",
                // color: "#00d27a",
                fontWeight: 500,
                fontFamily: "Inter",
                fontSize: 13,
                userSelect: "none",
              }}
            >
              {showAllMeta
                ? "Show less"
                : `Show more (${metaItems.length - visibleCount})`}
            </Text>
          )}
        </div>

        {/* Description block */}
        <div style={{ marginBottom: 24 }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#6b7280",
              fontSize: 13,
              marginBottom: 8,
              fontFamily: "Inter",
            }}
          >
            <FileTextOutlined />
            <span>Description</span>
          </div>

          {/* Content */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              padding: 12,
              background: "#fafafa",
            }}
          >
            <div
              style={{ color: "#374151", fontSize: 13, margin: 0, padding: 0 }}
              dangerouslySetInnerHTML={{
                __html: task.description || "<p>No description provided</p>",
              }}
            />
          </div>
        </div>

        {/* Acceptance Criteria */}
        {task.acceptanceCriteria && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#6b7280",
                fontSize: 13,
                marginBottom: 8,
              }}
            >
              <FileTextOutlined />
              <span>Acceptance Criteria</span>
            </div>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 4,
                padding: 12,
                background: "#fafafa",
              }}
            >
              <div
                style={{
                  color: "#374151",
                  fontSize: 13,
                  margin: 0,
                  padding: 0,
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    task.acceptanceCriteria ||
                    "<p>No acceptance criteria provided</p>",
                }}
              />
            </div>
          </div>
        )}

        {/* Attachments */}
        <AttachmentsSection links={taskLinks || []} onAddLink={handleAddLink} />

        {/* Tabs */}
        <Tabs
          defaultActiveKey="comments"
          items={[
            {
              key: "comments",
              label: `Comments (${task.comments.length})`,
              children: (
                <CommentsSection
                  comments={comments}
                  onAdd={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  loading={loading}
                  user={user}
                />
              ),
            },
            {
              key: "activity",
              label: "Activity",
              children: (
                <Text type="secondary">Activity log will appear here.</Text>
              ),
            },
          ]}
        />
      </div>
    </Drawer>
  );
};

/* Helper Components */
const MetaRow = ({ icon, label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      marginBottom: 14,
    }}
  >
    {/* Left label */}
    <div
      style={{
        // width: 160,
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        fontSize: 13,
        flex: 1,
      }}
    >
      {icon}
      <span>{label}</span>
    </div>

    {/* Right value */}
    <div style={{ flex: 2 }}>{value}</div>
  </div>
);

export default TaskDrawer;
